/**
 * ORDER BOOK — broker-terminal view: ONE ROW PER INDIVIDUAL LEG ORDER.
 *
 * Trade Book stays the trade-level summary (one row per execution). This page is the
 * granular counterpart: every entry leg and every exit leg gets its own row, with the
 * Far-First sequence preserved (L3 · Seq 1 = far leg, Seq 2 = near leg).
 *
 * One page serves PAPER now and LIVE later — Environment is a column and a filter, never a
 * separate screen.
 */
import { scannerNameDiffers } from "@/lib/scannerName";
import React, { useCallback, useEffect, useState } from "react";
import api, { detailText } from "@/lib/api";
import { inr, num, istDate, istTime, istYmd, dateTime } from "@/lib/format";
import { ScrollText, X, ArrowUpDown } from "lucide-react";
import { scopeParams } from "@/components/BookScope";
import ReportFilters, { DEFAULT_REPORT_FILTERS, reportParams, Sel } from "@/components/ReportFilters";
import PageCount, { PAGE_STEP } from "@/components/PageCount";
import { qtyLabel } from "@/lib/qty";
import { visibleInterval } from "@/lib/poll";

const STATUS_CLS = {
  COMPLETE: "bg-[#00C805]/15 text-[#00C805]",
  PENDING: "bg-[#FFCC00]/15 text-[#FFCC00]",
  OPEN: "bg-[#2563EB]/15 text-[#5b9bff]",
  "TRIGGER PENDING": "bg-[#FFCC00]/15 text-[#FFCC00]",
  PARTIAL: "bg-[#FFCC00]/15 text-[#FFCC00]",
  CANCELLED: "bg-[#475569]/25 text-slate-400",
  REJECTED: "bg-[#FF6b62]/15 text-[#FF6b62]",
  FAILED: "bg-[#FF6b62]/15 text-[#FF6b62]",
};
const ACTION_CLS = {
  ENTRY: "bg-[#2563EB]/15 text-[#5b9bff]",
  EXIT: "bg-[#7c3aed]/20 text-[#c4b5fd]",
};

const COLS = [
  ["Date", "time"], ["Time (IST)", null], ["Trade", "trade"], ["Market", null], ["Underlying", null],
  ["Level", "level"], ["Pair", "pair"], ["Leg", null], ["Seq", null],
  ["Symbol (exact)", "symbol"], ["Side", null],
  ["Action", null], ["Qty", "qty"], ["Lots", null], ["Type", null],
  ["Req. Price", null], ["Avg Fill", null], ["Filled", null], ["Pending", null],
  ["Order Value", null], ["Status", "status"], ["Env", null], ["Order ID", null],
  ["Broker ID", null], ["Message", null],
];

function Row({ label, value, cls }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-3 py-0.5 border-b border-[#20242c]">
      <span className="text-slate-500">{label}</span>
      <span className={`mono text-right ${cls || "text-slate-200"}`}>{value}</span>
    </div>
  );
}

function OrderDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-[560px] max-w-full h-full overflow-auto bg-[#12151C] border-l border-[#2A2F3A] p-5"
        onClick={(e) => e.stopPropagation()} data-testid="order-detail">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">Order Detail</div>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        {d === false ? <div className="text-xs text-[#FF6b62]">Order not found.</div>
          : !d ? <div className="text-xs text-slate-500">Loading…</div> : (
          <div className="space-y-4 text-xs">
            <div className="term-panel-2 rounded-sm p-3">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${d.order.side === "SELL" ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#5b9bff]/15 text-[#5b9bff]"}`}>{d.order.side}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${ACTION_CLS[d.order.action] || "bg-[#1A1D24] text-slate-400"}`}>{d.order.action_label}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${STATUS_CLS[d.order.status] || "bg-[#1A1D24] text-slate-400"}`}>{d.order.status}</span>
                <span className="text-[10px] mono text-slate-400">{d.order.market}</span>
              </div>
              <div className="mono text-[11px] text-fg mb-2">{d.order.symbol}</div>
              <Row label="Trade" value={d.order.trade_name} />
              {d.order.pair_contracts && <Row label={`Pair ${d.order.pair_no} contracts`} value={d.order.pair_contracts} />}
              <Row label="Execution ID" value={d.order.execution_id} />
              <Row label="Level · Seq" value={d.order.level_no != null ? `L${d.order.level_no}${d.order.sequence_no ? ` · Seq ${d.order.sequence_no}` : ""}` : null} />
              <Row label="Pair · Leg" value={d.order.pair_no != null ? `Pair ${d.order.pair_no} · ${d.order.leg_role || "—"}` : null} />
              <Row label="Granularity" value={d.order.execution_granularity} />
              <Row label="Idempotency key" value={d.order.idempotency_key || d.order.attempted_idempotency_key} />
              <Row label="Underlying" value={d.order.underlying} />
              <Row label="Qty / Lots" value={`${qtyLabel(d.order)}${d.order.lots ? ` (${d.order.lots} lot${d.order.lots === 1 ? "" : "s"})` : ""}`} />
              <Row label="Order Type" value={d.order.order_type} />
              <Row label="Limit Price" value={d.order.limit_price != null ? num(d.order.limit_price, 2) : null} />
              <Row label="Trigger Price" value={d.order.trigger_price != null ? num(d.order.trigger_price, 2) : null} />
              <Row label="LTP at decision" value={d.order.requested_price != null ? num(d.order.requested_price, 2) : null} />
              <Row label="Average Fill" value={d.order.average_fill_price != null ? num(d.order.average_fill_price, 2) : null} />
              <Row label="Slippage" value={d.order.slippage != null ? num(d.order.slippage, 4) : null}
                cls={d.order.slippage < 0 ? "text-[#FF6b62]" : "text-[#00C805]"} />
              <Row label="Filled / Pending" value={`${d.order.filled_quantity} / ${d.order.pending_quantity}`} />
              {d.order.fill_source === "SIMULATED" && <>
                <Row label="Fill source" value={`SIMULATED (SHADOW) · ${d.order.simulated_status}${d.order.simulated_close_reason ? ` · ${d.order.simulated_close_reason}` : ""}`} cls="text-[#c9a227]" />
                <Row label="Broker filled / pending" value={`${d.order.broker_filled_quantity} / ${d.order.broker_pending_quantity ?? "—"} (${d.order.broker_status || "—"})`} />
              </>}
              {d.order.fill_source === "BROKER" && <Row label="Fill source" value="BROKER" />}
              <Row label="Order Value" value={d.order.order_value != null ? inr(d.order.order_value) : null} />
              <Row label="Environment" value={`${d.order.environment} · ${d.order.execution_provider || "—"}`} />
              <Row label="Internal Order ID" value={d.order.internal_order_id} />
              <Row label="Broker Order ID" value={d.order.broker_order_id || (d.order.environment === "LIVE" ? "— (not submitted)" : "— (PAPER)")} />
              {/* LIVE submission identity. `submit_state` is the honest answer to "what happened
                  when we sent it": AMBIGUOUS means we do not know and it is NOT retried. */}
              {d.order.environment === "LIVE" && <>
                <Row label="Submit state" value={d.order.submit_state}
                  cls={d.order.submit_state === "AMBIGUOUS" ? "text-[#FFCC00]"
                    : d.order.submit_state === "REJECTED" ? "text-[#FF6b62]" : "text-slate-300"} />
                <Row label="Broker tag" value={d.order.broker_tag} />
                <Row label="Exchange Order ID" value={d.order.exchange_order_id} />
                <Row label="Broker status" value={d.order.broker_status} />
                <Row label="Broker message" value={d.order.status_message} />
                <Row label="Submit attempts" value={d.order.submit_attempts} />
                <Row label="Broker quantity" value={d.order.broker_quantity} />
                <Row label="P&L multiplier" value={d.order.pnl_multiplier} />
                <Row label="Product / Variety / Validity"
                  value={[d.order.product, d.order.variety, d.order.validity].filter(Boolean).join(" · ") || null} />
                <Row label="Intent written" value={d.order.intent_at ? dateTime(d.order.intent_at) : null} />
                <Row label="Submitted" value={d.order.submitted_at ? dateTime(d.order.submitted_at) : null} />
                <Row label="Ambiguous at" value={d.order.ambiguous_at ? dateTime(d.order.ambiguous_at) : null} />
                <Row label="Resolved at" value={d.order.resolved_at ? dateTime(d.order.resolved_at) : null} />
              </>}
              <Row label="Order date (IST)" value={istDate(d.order.order_date || istYmd(d.order.timestamp))} />
              <Row label="Order time (IST)" value={d.order.order_time || istTime(d.order.timestamp)} />
              <Row label="Created (UTC → IST)" value={dateTime(d.order.timestamp)} />
              <Row label="Placed" value={d.order.placed_at ? dateTime(d.order.placed_at) : null} />
              <Row label="Filled" value={d.order.filled_at ? dateTime(d.order.filled_at) : null} />
              <Row label="Rejected" value={d.order.rejected_at ? dateTime(d.order.rejected_at) : null} />
              <Row label="Fill ID" value={d.fill?.id} />
              <Row label="Message" value={d.order.message} cls="text-[#FF6b62]" />
            </div>

            {/* A LIVE order can fill MANY times. Each row here is a real exchange trade, keyed by
                the broker's own trade id — the Trade Book is built from these and nothing else. */}
            {d.broker_fills?.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-1">
                  Broker fills ({d.broker_fills.length})
                </div>
                <div className="term-panel-2 rounded-sm p-2 space-y-1">
                  {d.broker_fills.map((f) => (
                    <div key={f.broker_trade_id} className="flex justify-between gap-2 mono text-[10px]">
                      <span className="text-slate-400">{f.broker_trade_id}</span>
                      <span className="text-slate-300">{f.quantity} @ {num(f.price, 2)}</span>
                      <span className="text-slate-600">{f.fill_timestamp || istTime(f.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.order.order_value != null && (
              <div className="text-[10px] text-slate-600">
                Order Value = fill price × filled qty (options: × units per quantity; the limit sent while nothing is filled). Informational notional — not margin blocked.
              </div>
            )}

            {d.events?.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-1">Broker response / state history</div>
                <div className="term-panel-2 rounded-sm p-2 space-y-1">
                  {d.events.map((e) => (
                    <div key={e.id} className={`flex justify-between gap-2 mono text-[10px] ${e.ignored ? "opacity-60" : ""}`}>
                      <span className={e.ignored ? "text-slate-500 line-through" : "text-slate-300"}>{e.state}</span>
                      {/* where the update came from: WS is a hint, POLL/SWEEP are the broker's
                          own answer. An IGNORED line means the packet arrived late or repeated
                          and was deliberately not applied. */}
                      {e.source && <span className={`text-[9px] px-1 rounded-sm ${e.ignored
                        ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "bg-[#1A1D24] text-slate-500"}`}>
                        {e.ignored ? `IGNORED · ${e.ignored_reason}` : e.source}
                      </span>}
                      <span className="text-slate-500">{JSON.stringify(e.detail ?? "")}</span>
                      <span className="text-slate-600">{istTime(e.event_at || e.ts)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.level && (
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-1">Level {d.level.level_number}</div>
                <div className="term-panel-2 rounded-sm p-2">
                  <Row label="Status" value={d.level.level_status} />
                  <Row label="SELL entry" value={d.level.sell_entry_price != null ? num(d.level.sell_entry_price, 2) : null} />
                  <Row label="BUY entry" value={d.level.buy_entry_price != null ? num(d.level.buy_entry_price, 2) : null} />
                  <Row label="Entry spread" value={d.level.entry_spread != null ? num(d.level.entry_spread, 4) : null} />
                  <Row label="Reference spread" value={d.level.reference_spread != null ? num(d.level.reference_spread, 4) : null} />
                  <Row label="Next threshold" value={d.level.next_level_threshold != null ? num(d.level.next_level_threshold, 4) : null} />
                  <Row label="Level P&L" value={d.level.level_pnl != null ? inr(d.level.level_pnl) : null} />
                  <Row label="Exit reason" value={d.level.exit_reason} />
                </div>
              </div>
            )}

            {d.audit?.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-1">Associated audit events</div>
                <div className="term-panel-2 rounded-sm p-2 space-y-1 max-h-52 overflow-auto">
                  {d.audit.map((a) => (
                    <div key={a.id} className="mono text-[10px]">
                      <span className="text-slate-300">{a.event}</span>
                      <span className="text-slate-600 ml-2">{istTime(a.ts)}</span>
                      {a.detail && <div className="text-slate-500 pl-2">{JSON.stringify(a.detail).slice(0, 160)}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const simTag = (o) => (o.fill_source === "SIMULATED" && o.filled_quantity > 0
  ? <span className="ml-1 text-[8px] font-bold text-[#c9a227]" title="Simulated SHADOW fill - not a broker execution">SIM</span> : null);

export default function OrderBook({ embedded = false, scope = null }) {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [rf, setRf] = useState(DEFAULT_REPORT_FILTERS);
  const [limit, setLimit] = useState(PAGE_STEP);
  const [f, setF] = useState({ market: "ALL", action: "ALL", granularity: "ALL", status: "ALL",
    sort: "time", direction: "desc" });

  const load = useCallback(() => {
    const base = reportParams(rf, { ...f, limit });
    if (!base) return;                                     // custom range not chosen yet
    const params = scope ? { ...base, environment: undefined, ...scopeParams(scope) } : base;
    api.get("/orders", { params })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(detailText(e, "Could not load orders")));
  }, [f, rf, limit, scope?.product, scope?.environment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); return visibleInterval(load, 4000); }, [load]);
  useEffect(() => { setLimit(PAGE_STEP); }, [f, rf]);              // new filters start from the first page

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSort = (key) => {
    if (!key) return;
    setF((p) => ({ ...p, sort: key, direction: p.sort === key && p.direction === "desc" ? "asc" : "desc" }));
  };

  if (!data) return <div className="p-6 text-slate-500">{error || "Loading Order Book…"}</div>;

  return (
    <div className={embedded ? "space-y-3" : "p-4 space-y-3"}>
      {detail && <OrderDetail id={detail} onClose={() => setDetail(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-2">
        {embedded ? <span className="text-xs text-fg-secondary">One row per individual leg order</span> : (
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-[#5b9bff]" /> Order Book
          <span className="text-xs font-normal ml-2 text-slate-500">one row per individual leg order</span>
        </h1>)}
        <PageCount shown={data.shown ?? (data.orders || []).length} total={data.total ?? data.count} noun="orders"
          onMore={() => setLimit((l) => l + PAGE_STEP)} testId="order-count" />
      </div>

      <ReportFilters value={rf} onChange={setRf} hideEnvironment={!!scope}
        trades={(data.trades || []).map(([id, name]) => [id, name])}>
        <Sel label="Market" value={f.market} onChange={set("market")} testid="filter-market"
          options={[["ALL", "All"], ["MCX", "MCX"], ["NFO", "NFO"]]} />
        <Sel label="Action" value={f.action} onChange={set("action")} testid="filter-action"
          options={[["ALL", "All"], "ENTRY", "EXIT"]} />
        <Sel label="Status" value={f.status} onChange={set("status")} testid="filter-status"
          options={[["ALL", "All"], ...(data.statuses || [])]} />
      </ReportFilters>
      {error && <div className="text-[11px] text-[#FF6b62]" data-testid="order-book-error">{error}</div>}

      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 230px)" }}>
        <table className="w-full text-[11px] mono border-collapse">
          <thead className="sticky top-0 z-10 bg-[#1A1D24] text-slate-400">
            <tr>{COLS.map(([h, key]) => (
              <th key={h} onClick={() => toggleSort(key)}
                className={`px-2 py-2 text-left font-semibold whitespace-nowrap ${key ? "cursor-pointer hover:text-white" : ""}`}>
                {h}{key && <ArrowUpDown className="inline h-2.5 w-2.5 ml-1 opacity-40" />}
                {f.sort === key && <span className="ml-0.5 text-[#5b9bff]">{f.direction === "desc" ? "↓" : "↑"}</span>}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {data.orders.length === 0 && (
              <tr><td colSpan={COLS.length} className="px-3 py-6 text-center text-slate-600">No orders match these filters.</td></tr>
            )}
            {data.orders.map((o) => (
              <tr key={o.id} onClick={() => setDetail(o.internal_order_id || o.id)}
                className="gz-row cursor-pointer"
                data-testid={`order-row-${o.internal_order_id}`}>
                <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap" data-testid="order-date">{istDate(o.order_date || istYmd(o.timestamp))}</td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap" data-testid="order-time">{o.order_time || istTime(o.timestamp)}</td>
                <td className="px-2 py-1.5 text-fg font-semibold whitespace-nowrap">{o.trade_name || "—"}
                  {scannerNameDiffers(o.trade_name, [o.tradingsymbol || o.symbol, ...String(o.pair_contracts || "").split(" / ")]) && <span className="ml-1 text-[9px] font-normal text-[var(--term-muted)]"
                    data-testid={`order-scanner-name-${o.id}`} title="The trade is named after its scanner pair; the strikes executed are below">(scanner pair)</span>}
                  {o.pair_contracts && <div className="text-[10px] font-normal mono text-[#5b9bff]" data-testid={`order-pair-contracts-${o.id}`}
                    title="The contracts this order's pair actually traded (a SELL pair may use different strikes from the trade's name)">
                    P{o.pair_no} executed: {o.pair_contracts}</div>}</td>
                <td className="px-2 py-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${o.market_segment === "NFO" ? "bg-[#5b9bff]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>{o.market}</span>
                </td>
                <td className="px-2 py-1.5 text-slate-200">{o.underlying || "—"}</td>
                <td className="px-2 py-1.5 text-slate-300">{o.level_no != null ? `L${o.level_no}` : "—"}</td>
                {/* PAIR rows carry a pair number; level-sized and legacy rows show a dash
                    rather than a fabricated "pair 1". */}
                <td className="px-2 py-1.5 text-slate-300">{o.pair_no != null ? `P${o.pair_no}` : "—"}</td>
                <td className="px-2 py-1.5">
                  {o.leg_role
                    ? <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${o.leg_role === "FAR" ? "bg-[#7c3aed]/20 text-[#c4b5fd]" : "bg-[#2A2F3A] text-slate-300"}`}>{o.leg_role}</span>
                    : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-2 py-1.5 text-slate-400">{o.sequence_no != null ? `Seq ${o.sequence_no}` : "—"}</td>
                <td className={`px-2 py-1.5 whitespace-nowrap ${o.side === "SELL" ? "text-[#FF6b62]" : "text-[#5b9bff]"}`}>{o.symbol}</td>
                <td className="px-2 py-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${o.side === "SELL" ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#5b9bff]/15 text-[#5b9bff]"}`}>{o.side}</span>
                </td>
                <td className="px-2 py-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${ACTION_CLS[o.action] || "bg-[#1A1D24] text-slate-400"}`}
                    title={o.exit_reason || o.action}>{o.action_label}</span>
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap" data-testid="order-qty">{qtyLabel(o)}</td>
                <td className="px-2 py-1.5 text-right text-slate-400">{o.lots ?? "—"}</td>
                <td className="px-2 py-1.5 text-slate-400">{o.order_type}</td>
                <td className="px-2 py-1.5 text-right text-slate-400" data-testid="order-req-price"
                  title={o.requested_price != null ? `LTP at decision ${num(o.requested_price, 2)}` : undefined}>{o.price_sent != null ? num(o.price_sent, 2) : "—"}</td>
                <td className="px-2 py-1.5 text-right text-slate-100" data-testid="order-avg-fill">{o.average_fill_price != null ? num(o.average_fill_price, 2) : "—"}{simTag(o)}</td>
                <td className="px-2 py-1.5 text-right" data-testid="order-filled">{o.filled_quantity}{simTag(o)}</td>
                <td className="px-2 py-1.5 text-right text-slate-500">{o.pending_quantity}</td>
                <td className="px-2 py-1.5 text-right text-slate-300">{o.order_value != null ? inr(o.order_value) : "—"}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${STATUS_CLS[o.status] || "bg-[#1A1D24] text-slate-400"}`}
                    title={o.fill_source === "SIMULATED" ? "Zerodha's own status. The fill shown is SIMULATED (SHADOW)." : undefined}>
                    {o.fill_source === "SIMULATED" ? `Broker ${o.status}` : o.status}</span>
                  {o.fill_source === "SIMULATED" && <span data-testid="order-sim-status"
                    className="ml-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-[#9a7200]/15 text-[#c9a227]">SIM {o.simulated_status}</span>}
                </td>
                <td className="px-2 py-1.5 text-slate-500">{o.environment}</td>
                <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{o.internal_order_id}</td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{o.broker_order_id || "—"}</td>
                <td className="px-2 py-1.5 text-[#FF6b62] max-w-[220px] truncate" title={o.message || ""}>{o.message || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-600">
        Dates and times are IST (stored UTC timestamps are authoritative). One row per individual leg order — Far-First sequence preserved (Seq 1 = far contract, Seq 2 = near).
        Trade Book remains the trade-level summary. Order Value = fill price × filled qty (informational notional, not margin; options orders: × units per quantity, at the limit sent while nothing is filled).
        SHADOW options orders: Zerodha refuses them (status "Broker REJECTED") and the fill shown, marked SIM, is simulated against the live order book — not a broker execution.
        PAPER orders carry an internal order id and no broker order id. A LIVE order shows its broker order id,
        broker tag and submit state — where AMBIGUOUS means the submission's outcome is unknown and the order is
        deliberately NOT retried until the broker has been asked what it holds under that tag. Click any row for full detail.
      </div>
    </div>
  );
}
